"use client";

import logger from "@/utils/logger";

import React from "react";
import { Button, ProgressBar } from "react-bootstrap";
import AppDialog from "@/components/ui/AppDialog";

interface InactivityWarningDialogProps {
    show: boolean;
    timeRemaining: number; // in milliseconds
    onExtendSession: () => void;
    onLogoutNow: () => void;
    warningDuration?: number; // total warning window (ms) used for progress/copy
}

export const InactivityWarningDialog: React.FC<
    InactivityWarningDialogProps
> = ({
    show,
    timeRemaining,
    onExtendSession,
    onLogoutNow,
    warningDuration,
}) => {
    // Convert milliseconds to minutes and seconds for display
    const minutes = Math.floor(timeRemaining / 60000);
    const seconds = Math.floor((timeRemaining % 60000) / 1000);

    // Calculate progress percentage based on provided warningDuration
    // If not provided, assume 5 minutes as a sane default
    const total = warningDuration ?? 5 * 60 * 1000;
    // Design shows left-to-right fill as time elapses
    const elapsedPct = Math.max(
        0,
        Math.min(100, ((total - timeRemaining) / total) * 100)
    );

    // Debug logging
    React.useEffect(() => {
        if (show) {
            logger.info(
                "⏳ Dialog render - timeRemaining:",
                timeRemaining,
                "ms =",
                Math.floor(timeRemaining / 1000),
                "seconds"
            );
            logger.info(
                "🔍 Dialog render - minutes:",
                minutes,
                "seconds:",
                seconds
            );
            logger.info("🔍 Dialog render - elapsed%:", elapsedPct);
        }
    }, [show, timeRemaining, minutes, seconds, elapsedPct]);

    return (
        <AppDialog
            show={show}
            onHide={() => {}} // Prevent closing via backdrop/ESC
            title={
                <div
                    className="d-flex align-items-center"
                    style={{ fontSize: "18px", fontWeight: 700 }}
                >
                    Session Expiring
                </div>
            }
            size="sm" // Keep small size
            backdrop="static"
            closeButton={true}
            footer={
                <div className="d-flex justify-content-center gap-3 w-100 inactivity-dialog-footer">
                    <Button
                        variant="outline-secondary"
                        onClick={onLogoutNow}
                        className="inactivity-dialog-signout-btn"
                    >
                        Sign out
                    </Button>

                    <Button
                        variant="primary"
                        onClick={onExtendSession}
                        className="inactivity-dialog-continue-btn"
                    >
                        Continue Session
                    </Button>
                </div>
            }
        >
            <div className="inactivity-dialog-body">
                {/* Top text row */}
                <div className="d-flex align-items-start gap-2 mb-3">
                    <i
                        className="bi bi-clock inactivity-dialog-clock-icon"
                        style={{
                            fontSize: 20,
                            marginTop: "2px",
                        }}
                    />
                    <div className="inactivity-dialog-message">
                        Your session will automatically end due to inactivity.
                    </div>
                </div>

                {/* Countdown row */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <div className="inactivity-dialog-label">
                        Auto-logout in
                    </div>
                    <div className="inactivity-dialog-timer" aria-live="polite">
                        {minutes > 0
                            ? `${minutes}m ${seconds}s`
                            : `${seconds}s`}
                    </div>
                </div>

                {/* Progress */}
                <ProgressBar
                    now={elapsedPct}
                    variant="warning"
                    className="inactivity-dialog-progress"
                    style={{
                        height: 6,
                        borderRadius: 3,
                        marginBottom: "16px",
                    }}
                />
            </div>

            {/* Custom animations */}
            <style jsx>{`
                @keyframes pulse {
                    0% {
                        transform: scale(1);
                        opacity: 1;
                    }
                    50% {
                        transform: scale(1.1);
                        opacity: 0.8;
                    }
                    100% {
                        transform: scale(1);
                        opacity: 1;
                    }
                }

                :global(.modal-footer) {
                    border-top: none !important;
                    padding: 0 !important;
                }

                :global(.progress-bar) {
                    transition: width 0.3s ease-in-out !important;
                }
                /* Blue header like the design */
                :global(.modal-header.bg-primary) {
                    background-color: #3b82f6 !important;
                    border-top-left-radius: 8px !important;
                    border-top-right-radius: 8px !important;
                    padding: 16px 20px !important;
                }
                :global(.modal-content) {
                    border-radius: 8px !important;
                    border: none !important;
                }
                :global(.modal-body) {
                    padding: 0 !important;
                }
                :global(.modal-title) {
                    font-size: 18px !important;
                    font-weight: 600 !important;
                }
                :global(.modal-dialog) {
                    max-width: 400px !important;
                    width: 400px !important;
                }
            `}</style>
        </AppDialog>
    );
};

export default InactivityWarningDialog;
